import PropTypes from 'prop-types';
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import s from './WorkTracker.module.scss';
import { personShape, taskShape } from './propTypes';
import StatusSelect from './StatusSelect';
import DescriptionEditor from './DescriptionEditor';
import Checklist from './Checklist';
import Comments from './Comments';
import ConfirmDialog from './ConfirmDialog';
import { PRIORITIES } from './lib/sorting';
import { formatDateTime } from './lib/dates';
import { PencilIcon } from './icons';

const DISCARD = {
  title: 'Discard unsaved changes?',
  text: 'You have edits that have not been saved. Closing now will throw them away.',
  yes: 'Discard changes',
  no: 'Keep editing',
};

// The task details, in a centred dialog. Mount it with key={task.id}: the
// editable fields (assignee, due date, priority, and the title once its
// pencil is pressed) are seeded from the task once, so a refresh from the other
// person never overwrites what is being typed. Status, checklist and comments
// save straight away; the fields save with the button.
//
// Closing (backdrop, ×, Escape) asks first when anything is unsaved: an edited
// field, an open description edit, or a half-typed comment or checklist step.
// Each editor reports its own unsaved state through `onDirty`.
export default function TaskDialog({
  task, people, onClose, onStatus, onSaveFields, onSaveDescription,
  onChecklist, onAddComment, onDeleteComment, onDelete, onError,
}) {
  const [fields, setFields] = useState({
    title: task.title,
    assignee_id: task.assignee_id || '',
    due_on: task.due_on || '',
    priority: task.priority,
  });
  const [saving, setSaving] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [dirtyParts, setDirtyParts] = useState({});
  const [confirmingClose, setConfirmingClose] = useState(false);
  const set = (k, v) => setFields((f) => ({ ...f, [k]: v }));
  const markDirty = useCallback((part) => (dirty) => (
    setDirtyParts((d) => (!!d[part] === !!dirty ? d : { ...d, [part]: !!dirty }))
  ), []);
  const onDescriptionDirty = useMemo(() => markDirty('description'), [markDirty]);
  const onChecklistDirty = useMemo(() => markDirty('checklist'), [markDirty]);
  const onCommentDirty = useMemo(() => markDirty('comment'), [markDirty]);

  const fieldsDirty = fields.title !== task.title
    || String(fields.assignee_id || '') !== String(task.assignee_id || '')
    || (fields.due_on || '') !== (task.due_on || '')
    || fields.priority !== task.priority;
  const dirty = fieldsDirty || Object.values(dirtyParts).some(Boolean);

  const requestClose = useCallback(() => {
    if (dirty) setConfirmingClose(true);
    else onClose();
  }, [dirty, onClose]);
  const keepEditing = useCallback(() => setConfirmingClose(false), []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !confirmingClose) requestClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [requestClose, confirmingClose]);

  // Leaving the page (reload, back, closing the tab) gets the browser's own
  // "unsaved changes" prompt for the same reasons.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const canSave = !saving && fields.title.trim().length > 0;
  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    const ok = await onSaveFields(task, {
      ...fields, title: fields.title.trim(), assignee_id: fields.assignee_id || null, due_on: fields.due_on || null,
    });
    setSaving(false);
    if (ok) setEditingTitle(false);
  };

  return (
    <>
      <div className={s.backdrop} onClick={requestClose} role="presentation" />
      <div className={s.modal} role="dialog" aria-modal="true" aria-labelledby="wt-dialog-title">
        <div className={s.drawerHead}>
          {editingTitle ? (
            <input
              id="wt-dialog-title"
              className={`${s.input} ${s.titleInput}`}
              aria-label="Title"
              value={fields.title}
              autoFocus // eslint-disable-line jsx-a11y/no-autofocus
              onChange={(e) => set('title', e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); save(); } }}
            />
          ) : (
            <div className={s.labelRow} style={{ margin: 0, minWidth: 0 }}>
              <h2 className={s.drawerTitle} id="wt-dialog-title">{task.title}</h2>
              <button
                type="button"
                className={s.iconEdit}
                aria-label="Edit title"
                title="Edit title"
                onClick={() => setEditingTitle(true)}
              >
                <PencilIcon />
              </button>
            </div>
          )}
          <button type="button" className={s.close} onClick={requestClose} aria-label="Close">×</button>
        </div>
        <div className={s.drawerBody}>
          <div className={s.fields}>
            <div className={s.fieldRow}>
              <div className={s.field}>
                <label className={s.label} htmlFor="wt-assignee">Assigned to</label>
                <select
                  id="wt-assignee"
                  className={s.select}
                  value={fields.assignee_id}
                  onChange={(e) => set('assignee_id', e.target.value)}
                >
                  <option value="">Unassigned</option>
                  {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className={s.field}>
                <label className={s.label} htmlFor="wt-due">Due date</label>
                <input
                  id="wt-due"
                  type="date"
                  className={s.input}
                  value={fields.due_on}
                  onChange={(e) => set('due_on', e.target.value)}
                />
              </div>
              <div className={s.field}>
                <label className={s.label} htmlFor="wt-priority">Priority</label>
                <select
                  id="wt-priority"
                  className={s.select}
                  value={fields.priority}
                  onChange={(e) => set('priority', e.target.value)}
                >
                  {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
            </div>
          </div>

          <DescriptionEditor
            key={task.id}
            task={task}
            onSave={(text, release) => onSaveDescription(task, text, release)}
            onError={onError}
            onDirty={onDescriptionDirty}
          />
          <Checklist task={task} onChange={(steps) => onChecklist(task, steps)} onDirty={onChecklistDirty} />
          <Comments
            task={task}
            onAdd={(body) => onAddComment(task, body)}
            onDelete={(c) => onDeleteComment(task, c)}
            onDirty={onCommentDirty}
          />

          <div className={s.drawerFoot}>
            <div className={s.inline}>
              <span className={s.label} style={{ margin: 0 }}>Status</span>
              <StatusSelect value={task.status} label="Status" onChange={(status) => onStatus(task, status)} />
            </div>
            <span className={s.spacer} />
            <button type="button" className={s.btnDanger} onClick={() => onDelete(task)}>Delete</button>
            <button type="button" className={s.btnPrimary} disabled={!canSave} onClick={save}>
              Save changes
            </button>
          </div>
          <p className={s.timestamps}>
            Added
            {' '}
            {formatDateTime(task.created_at)}
            {' '}
            · Updated
            {' '}
            {formatDateTime(task.updated_at)}
            {task.completed_at && (
            <>
              {' '}
              · Completed
              {formatDateTime(task.completed_at)}
            </>
            )}
          </p>
        </div>
      </div>
      <ConfirmDialog
        confirm={confirmingClose ? { ...DISCARD, onConfirm: onClose } : null}
        onClose={keepEditing}
      />
    </>
  );
}

TaskDialog.propTypes = {
  task: taskShape.isRequired,
  people: PropTypes.arrayOf(personShape).isRequired,
  onClose: PropTypes.func.isRequired,
  onStatus: PropTypes.func.isRequired,
  onSaveFields: PropTypes.func.isRequired,
  onSaveDescription: PropTypes.func.isRequired,
  onChecklist: PropTypes.func.isRequired,
  onAddComment: PropTypes.func.isRequired,
  onDeleteComment: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};
